import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import AddStaffDialog from './AddStaffDialog';
import EditStaffDialog from './EditStaffDialog';
import api from '../utils/api';
import './AdminStaffs.css';

const AdminStaffs = () => {
  const { adminUnitId, subunitId } = useParams();
  const location = useLocation();
  const [subunit, setSubunit] = useState(location.state?.subunit || null);
  const [adminUnit, setAdminUnit] = useState(location.state?.adminUnit || null);
  
  const [staffs, setStaffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [subunits, setSubunits] = useState([]);
  const [draggedStaff, setDraggedStaff] = useState(null);
  const [dragOverStaff, setDragOverStaff] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [contextResolved, setContextResolved] = useState(!!location.state?.subunit);
  


  const fetchStaffs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin-staffs?department_ref=${subunitId}`);
      
              if (response.data.success) {
          // Sort staff by priority if available, otherwise by name
          const sortedStaffs = response.data.data.sort((a, b) => {
            if (a.priority !== undefined && b.priority !== undefined) {
              return a.priority - b.priority;
            }
            return a.name.localeCompare(b.name);
          });
          setStaffs(sortedStaffs);
        } else {
        throw new Error(response.data.message || 'Failed to fetch staff');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch staff');
    } finally {
      setLoading(false);
    }
  }, [subunitId]);

  const fetchSubunits = useCallback(async () => {
    try {
      const response = await api.get(`/admin-subunits?administration_ref=${adminUnitId}`);
      
      if (response.data.success) {
        setSubunits(response.data.data);
        const matchedSubunit = response.data.data.find(item => item._id === subunitId);
        if (matchedSubunit) {
          setSubunit(matchedSubunit);
        }
        setContextResolved(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch subunits');
      setContextResolved(true);
      setLoading(false);
    }
  }, [adminUnitId, subunitId]);

  const fetchAdminUnit = useCallback(async () => {
    try {
      const response = await api.get('/admin-units');
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch admin unit');
      }

      const matchedAdminUnit = response.data.data.find(item => item._id === adminUnitId);
      if (!matchedAdminUnit) {
        throw new Error('Administration unit not found');
      }

      setAdminUnit(matchedAdminUnit);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch admin unit');
    }
  }, [adminUnitId]);

  useEffect(() => {
    if (!adminUnit && adminUnitId) {
      fetchAdminUnit();
    }
  }, [adminUnit, adminUnitId, fetchAdminUnit]);

  useEffect(() => {
    if (adminUnitId) {
      fetchSubunits();
    }
  }, [adminUnitId, fetchSubunits]);

  useEffect(() => {
    if (subunit) {
      fetchStaffs();
    } else if (contextResolved) {
      setLoading(false);
      setError('Administration subunit not found');
    }
  }, [contextResolved, subunit, fetchStaffs]);

  const handleAddStaff = async (staffData) => {
    try {
      if (!subunit?._id) {
        throw new Error('Administration subunit not found. Please reload and try again.');
      }

      const data = {
        ...staffData,
        department_ref: subunit._id
      };
      
      const response = await api.post('/admin-staffs', data);

      if (response.data.success) {
        setStaffs(prev => [...prev, response.data.data].sort((a, b) => {
          if (a.priority !== undefined && b.priority !== undefined) {
            return a.priority - b.priority;
          }
          return a.name.localeCompare(b.name);
        }));
        setShowAddDialog(false);
      } else {
        throw new Error(response.data.message || 'Failed to create staff');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create staff');
    }
  };

  const handleEditStaff = async (id, staffData) => {
    try {
      const response = await api.put(`/admin-staffs/${id}`, staffData);

      if (response.data.success) {
        setStaffs(prevStaffs => {
          const updated = prevStaffs.map(staff => 
            staff._id === id ? { ...staff, ...response.data.data } : staff
          );

          // Re-sort to maintain proper order after edit
          return updated.sort((a, b) => {
            if (a.priority !== undefined && b.priority !== undefined) {
              return a.priority - b.priority;
            }
            return a.name.localeCompare(b.name);
          });
        });
        setShowEditDialog(false);
        setSelectedStaff(null);
      } else {
        throw new Error(response.data.message || 'Failed to update staff');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update staff');
    }
  };

  const handleDeleteStaff = async (id) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) {
      return;
    }

    try {
      const response = await api.delete(`/admin-staffs/${id}`);
      
      if (response.data.success) {
        setStaffs(staffs.filter(staff => staff._id !== id));
      } else {
        throw new Error(response.data.message || 'Failed to delete staff');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete staff');
    }
  };

  const openEditDialog = (staff) => {
    setSelectedStaff(staff);
    setShowEditDialog(true);
  };



  // Drag and Drop handlers
  const handleDragStart = (e, staff) => {
    if (searchTerm.trim()) {
      return;
    }

    setDraggedStaff(staff);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
  };

  const handleDragOver = (e, staff) => {
    if (searchTerm.trim()) {
      return;
    }

    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedStaff && draggedStaff._id !== staff._id) {
      setDragOverStaff(staff._id);
    }
  };

  const handleDrop = (e, targetStaff) => {
    if (searchTerm.trim()) {
      return;
    }

    e.preventDefault();
    
    if (!draggedStaff || draggedStaff._id === targetStaff._id) {
      return;
    }

    const draggedIndex = staffs.findIndex(s => s._id === draggedStaff._id);
    const targetIndex = staffs.findIndex(s => s._id === targetStaff._id);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    // Create new array with reordered staff
    const newStaffs = [...staffs];
    const [removed] = newStaffs.splice(draggedIndex, 1);
    newStaffs.splice(targetIndex, 0, removed);

    // Update priorities based on new order
    const updatedStaffs = newStaffs.map((staff, index) => ({
      ...staff,
      priority: index + 1
    }));

    setStaffs(updatedStaffs);
    setDraggedStaff(null);
    setDragOverStaff(null);
    setIsReordering(true);
  };

  const handleDragEnd = () => {
    setDraggedStaff(null);
    setDragOverStaff(null);
  };

  // Send updated priorities to backend
  const sendUpdatedPriorities = useCallback(async () => {
    if (!isReordering) {
      return;
    }

    try {
      const priorities = staffs.map(staff => ({
        _id: staff._id,
        priority: staff.priority
      }));

      const response = await api.put('/admin-staffs-priorities', {
        dept_ref: subunit._id,
        priorities: priorities
      });

      if (response.data.success) {
        setIsReordering(false);
      } else {
        // Revert to original order if update fails
        fetchStaffs();
      }
    } catch (err) {
      // Revert to original order if update fails
      fetchStaffs();
    } finally {
      setIsReordering(false);
    }
  }, [staffs, isReordering, subunit._id, fetchStaffs]);

  // Auto-save priorities after reordering
  useEffect(() => {
    if (isReordering) {
      const timeoutId = setTimeout(() => {
        sendUpdatedPriorities();
      }, 1000); // Wait 1 second after last reorder

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isReordering, sendUpdatedPriorities]);

  const filteredStaffs = staffs.filter(staff =>
    staff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.designation.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canReorder = !searchTerm.trim() && filteredStaffs.length > 1;

  if (!subunit && !loading) return null;

  if (loading) {
    return (
      <div className="staffs-container">
        <div className="loading-spinner">Loading staff...</div>
      </div>
    );
  }

  return (
    <div className="staffs-container">
      <div className="staffs-header">
        <div className="header-left">
          <h2>Staff Members - {subunit.name}</h2>
          <p className="subtitle">{subunit.nameEn}</p>
          {canReorder && (
            <p className="reorder-hint">Drag and drop staff cards to reorder them</p>
          )}
          {searchTerm.trim() && staffs.length > 1 && (
            <p className="search-hint">Clear search to reorder staffs</p>
          )}
        </div>
        <button 
          className="add-button"
          onClick={() => setShowAddDialog(true)}
        >
          + Add New Staff
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search staff by name, email, or designation..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>



      <div className="staffs-grid">
        {filteredStaffs.length === 0 ? (
          <div className="no-staffs">
            {searchTerm ? 'No staff found matching your search.' : 'No staff members found for this subunit.'}
          </div>
        ) : (
          <>
            {filteredStaffs.map((staff) => (
              <div
                key={staff._id}
                className={`staff-card ${draggedStaff?._id === staff._id ? 'dragging' : ''} ${dragOverStaff === staff._id ? 'drag-over' : ''}`}
                draggable={!searchTerm}
                onDragStart={(e) => handleDragStart(e, staff)}
                onDragOver={(e) => handleDragOver(e, staff)}
                onDrop={(e) => handleDrop(e, staff)}
                onDragEnd={handleDragEnd}
              >
                <div className="staff-card-header">
                  <div className="staff-avatar">
                    {staff.dp ? (
                      <img
                        src={staff.dp}
                        alt={staff.name}
                        className="avatar-image"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <span
                      className="avatar-text"
                      style={{ display: staff.dp ? 'none' : 'flex' }}
                    >
                      {staff.name.charAt(0)}
                    </span>
                  </div>
                  <div className="drag-icon-container" title={searchTerm ? 'Clear search to reorder' : 'Drag to reorder'}>
                    <span className="drag-icon">⋮⋮</span>
                  </div>
                </div>

                <div className="staff-content">
                  <div className="staff-info">
                    <h3 className="staff-name">{staff.name}</h3>
                    {staff.nameEn && (
                      <p className="staff-name-en">{staff.nameEn}</p>
                    )}
                    <p className="staff-designation">{staff.designation}</p>
                    {staff.email && (
                      <p className="staff-email">✉️ {staff.email}</p>
                    )}
                    {staff.phoneNumber && (
                      <p className="staff-phone">📞 {staff.phoneNumber}</p>
                    )}
                    {typeof staff.priority === 'number' && (
                      <p className="staff-priority">Priority {staff.priority}</p>
                    )}
                  </div>
                </div>

                <div className="staff-card-actions">
                  <button
                    onClick={() => openEditDialog(staff)}
                    className="edit-staff-btn"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteStaff(staff._id)}
                    className="delete-staff-btn"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            {isReordering && (
              <div className="reorder-status">
                <span className="status-indicator">⏳</span>
                Updating staff order...
              </div>
            )}
          </>
        )}
      </div>

      {showAddDialog && (
        <AddStaffDialog
          onClose={() => setShowAddDialog(false)}
          onAdd={handleAddStaff}
          subunitName={subunit.name}
          adminUnitName={adminUnit?.name}
          subunitId={subunit._id}
        />
      )}

      {showEditDialog && selectedStaff && (
        <EditStaffDialog
          staff={selectedStaff}
          subunits={subunits}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedStaff(null);
          }}
          onEdit={handleEditStaff}
        />
      )}
    </div>
  );
};

export default AdminStaffs;
